/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ViewEncapsulation } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RoutingService } from 'src/app/service/routing.service';
import { AuthService } from 'src/app/service/auth.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { EventsService } from 'src/app/shared/services/data-api/events.service';
import { SubEventsService } from 'src/app/shared/services/data-api/sub-events.service';
import { RelatedEntitiesService } from 'src/app/shared/services/data-api/related-entities.service';
import { PortfolioService } from 'src/app/shared/services/data-api/portfolio.service';
import { forkJoin } from 'rxjs';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-chartmap-info',
  templateUrl: './chartmap-info.component.html',
  styleUrls: ['./chartmap-info.component.scss'],
})
export class ChartmapInfoComponent {
  tabNames: any;
  infoLists: Record<string, any[]> = {};

  countries = [];
  selectedCountry: string;
  sectors = [];
  selectedSector: string;

  countries2 = [];
  selectedCountry2: string;
  sectors2 = [];
  selectedSector2: string;

  sourcesCountries = [];
  selectedSourceCountry: string;

  discoverGroups = [
    'Events',
    'Companies',
    'Countries',
    'Potential Entities',
    'Sources',
  ];
  selectedDiscoverGroup = 'Events';
  selectedTabIndex = 0;

  qparam: any;
  previousValue: any;
  activeTag: string;
  activeParticipant: string;
  activeEvent: any;

  nodata: boolean;
  loading: boolean;
  iready: boolean;

  /** Per-group loading state for inline spinners */
  groupLoading: Record<string, boolean> = {};

  /** Tracks which groups have been fetched for current qparam set */
  private fetched = new Set<string>();

  private isEventTypeRoute = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private relatedEntitiesService: RelatedEntitiesService,
    private portfolioService: PortfolioService,
    private eventsService: EventsService,
    private subEvents: SubEventsService,
    public routing: RoutingService,
    public auth: AuthService,
    private portfolioApi: PortfolioApiService,
  ) {
    this.route.queryParams.subscribe((qparam) => {
      this.isEventTypeRoute =
        qparam.type === 'sub_event' ||
        qparam.type === 'event' ||
        qparam.type === 'super_event';

      if (this.routing.isFolio() && !qparam.portfolioId) {
        this.tabNames = ['Portfolio', 'Discover'];
      } else if (this.isEventTypeRoute) {
        this.tabNames = ['Participants', 'Discover'];
      } else {
        this.tabNames = ['Discover'];
      }

      this.qparam = qparam;
      this.activeTag = decodeURI(qparam.tag).split('|')[0];
      this.activeParticipant = decodeURI(qparam.participant).split('|')[0];
      this.activeEvent = {
        type: decodeURI(qparam.eventype),
        id: decodeURI(qparam.eventid),
      };

      if (
        this.routing.reFresh(this.qparam, this.previousValue, [
          'type',
          'id',
          'time',
          'lang',
          'feed',
          'categories',
          'continents',
          'countries',
          'factuality',
          'temporality',
          'ranks',
          'panels',
          'period',
          'event_ids',
        ])
      ) {
        this.resetState();
        this.iready = true;
        this.fetchDefaultTab();
      }

      this.previousValue = this.qparam;
    });
  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;
    const tab = this.tabNames[index];
    if (tab === 'Portfolio' && !this.fetched.has('Portfolio')) {
      this.fetchPortfolio();
    }
    if (tab === 'Participants' && !this.fetched.has('Participants')) {
      this.fetchParticipants();
    }
    if (tab === 'Discover' && !this.fetched.has('Events')) {
      this.fetchEvents();
    }
  }

  onDiscoverGroupChange(group: string) {
    this.selectedDiscoverGroup = group;
    this.ensureGroupData(group);
  }

  filteredInfoLists(tab) {
    if (tab === 'Sources') {
      return (this.infoLists[tab] || []).filter((item) => {
        if (!this.selectedSourceCountry) return true;
        return item.countries
          ? item.countries.includes(this.selectedSourceCountry)
          : false;
      });
    } else if (tab === 'Companies') {
      return (this.infoLists[tab] || []).filter((item) => {
        const c =
          !this.selectedCountry || item.country?.value === this.selectedCountry;
        const s =
          !this.selectedSector || item.sector?.value === this.selectedSector;
        return c && s;
      });
    } else if (tab === 'Participants') {
      return (this.infoLists[tab] || []).filter((item) => {
        const c =
          !this.selectedCountry2 ||
          item.country?.value === this.selectedCountry2;
        const s =
          !this.selectedSector2 || item.sector?.value === this.selectedSector2;
        return c && s;
      });
    } else {
      return this.infoLists[tab] || [];
    }
  }

  goItem(item, type) {
    const returnParticipants =
      type === 'Participants' &&
      (this.qparam.eventype ||
        this.qparam.type === 'event' ||
        this.qparam.type === 'sub_event' ||
        this.qparam.type === 'super_event');
    if (type === 'Events') {
      this.iEvent(item);
    } else if (returnParticipants) {
      this.iParticipant(item);
    } else {
      this.iTag(item);
    }
  }

  // --- Private data fetching methods ---

  private resetState() {
    this.nodata = false;
    this.loading = true;
    this.iready = false;
    this.selectedDiscoverGroup = 'Events';
    this.selectedTabIndex = 0;
    this.fetched.clear();
    this.infoLists = {};
    this.groupLoading = {};
    this.countries = [];
    this.sectors = [];
    this.countries2 = [];
    this.sectors2 = [];
    this.sourcesCountries = [];
  }

  private fetchEvents() {
    this.groupLoading['Events'] = true;
    this.subEvents.fetchSubEventsList(this.qparam).subscribe(
      (result) => {
        this.infoLists['Events'] = result.events
          .filter((el) => el.count > 0)
          .map((el) => ({
            name: el.event.name,
            volume: el.count,
            id: el.event.id,
            type: el.event.type,
          }));
        this.fetched.add('Events');
        this.groupLoading['Events'] = false;
        this.loading = false;
      },
      () => {
        this.nodata = true;
        this.loading = false;
        this.groupLoading['Events'] = false;
      },
    );
  }

  private populateEventsFromItemized(
    eventsByParticipant: Map<
      string,
      {
        count: number;
        event: { id: string; name: string; type: 'sub_event' };
      }[]
    >,
  ): void {
    const totals = new Map<
      string,
      { name: string; volume: number; id: string; type: string }
    >();
    for (const events of eventsByParticipant.values()) {
      for (const row of events) {
        if (!row.count) continue;
        const existing = totals.get(row.event.id);
        if (existing) {
          existing.volume += row.count;
        } else {
          totals.set(row.event.id, {
            name: row.event.name,
            volume: row.count,
            id: row.event.id,
            type: row.event.type,
          });
        }
      }
    }
    this.infoLists['Events'] = Array.from(totals.values());
    this.fetched.add('Events');
  }

  /**
   * Fetch related entities (companies, PNEs, countries) in a single merged call.
   * Populates all three groups at once.
   */
  private fetchEntities() {
    this.groupLoading['Companies'] = true;
    this.groupLoading['Potential Entities'] = true;
    this.groupLoading['Countries'] = true;

    this.relatedEntitiesService
      .getRelatedEntities(this.qparam, ['company', 'pne', 'country'], 1000)
      .subscribe(
        (result) => {
          const entities = result as Record<string, any[]>;
          const companies = entities.company || [];
          const pnes = entities.pne || [];
          const countryEntities = entities.country || [];

          this.processCompanies(companies);
          this.infoLists['Potential Entities'] = pnes.map((el) => ({
            name: el.entity.name,
            volume: Math.round(el.sentiment.volume_ratio * 10) / 10,
            tag: 'm:' + el.entity.compound_key,
          }));
          this.infoLists['Countries'] = countryEntities.map((el) => ({
            name: el.entity.name,
            volume: Math.round(el.sentiment.volume_ratio * 10) / 10,
            tag: 'm:' + el.entity.compound_key,
          }));

          this.fetched.add('Companies');
          this.fetched.add('Potential Entities');
          this.fetched.add('Countries');
          this.groupLoading['Companies'] = false;
          this.groupLoading['Potential Entities'] = false;
          this.groupLoading['Countries'] = false;
        },
        () => {
          this.infoLists['Companies'] = [];
          this.infoLists['Potential Entities'] = [];
          this.infoLists['Countries'] = [];
          this.groupLoading['Companies'] = false;
          this.groupLoading['Potential Entities'] = false;
          this.groupLoading['Countries'] = false;
        },
      );
  }

  private processCompanies(companies: any[]) {
    const countriesFilter = [];
    const sectorsFilter = [];
    this.infoLists['Companies'] = companies.map((element) => {
      if (element.entity.description.Country) {
        countriesFilter.push(
          JSON.stringify({
            label: element.entity.description.Country.name,
            value: element.entity.description.Country.entity.alpha_id,
          }),
        );
      }
      if (element.entity.description.Sector) {
        sectorsFilter.push(
          JSON.stringify({
            label: element.entity.description.Sector.name,
            value: element.entity.description.Sector.entity.alpha_id,
          }),
        );
      }
      return {
        name: element.entity.name,
        country: element.entity.description.Country
          ? {
              name: element.entity.description.Country.name,
              value: element.entity.description.Country.entity.alpha_id,
            }
          : null,
        sector: element.entity.description.Sector
          ? {
              name: element.entity.description.Sector.name,
              value: element.entity.description.Sector.entity.alpha_id,
            }
          : null,
        volume: Math.round(element.sentiment.volume_ratio * 10) / 10,
        tag: 'm:' + element.entity.compound_key,
      };
    });
    this.countries = Array.from(new Set(countriesFilter)).map((item) =>
      JSON.parse(item),
    );
    this.sectors = Array.from(new Set(sectorsFilter)).map((item) =>
      JSON.parse(item),
    );
  }

  /**
   * Fetch top sources — only when Sources group is selected.
   */
  private fetchSources() {
    this.groupLoading['Sources'] = true;
    this.eventsService.getTopSources(this.qparam, 29).subscribe(
      (result) => {
        const sourcesCountriesFilter = [];
        this.infoLists['Sources'] = result.top_sources.map((element) => {
          if (element.countries) {
            element.countries.forEach((country) => {
              sourcesCountriesFilter.push(
                JSON.stringify({
                  label: country.name,
                  value: country.entity.alpha_id,
                }),
              );
            });
          }
          return {
            name: element.source.name,
            countries: element.countries
              ? element.countries.map((el) => el.entity.alpha_id)
              : [],
            volume: element.count,
            tag: 'm:' + element.source.type + ':' + element.source.alpha_id,
          };
        });
        this.sourcesCountries = Array.from(new Set(sourcesCountriesFilter)).map(
          (item) => JSON.parse(item),
        );
        this.fetched.add('Sources');
        this.groupLoading['Sources'] = false;
      },
      () => {
        this.infoLists['Sources'] = [];
        this.sourcesCountries = [];
        this.groupLoading['Sources'] = false;
      },
    );
  }

  /**
   * Fetch participants — only on Participants tab click (market + event type route).
   */
  private fetchParticipants() {
    this.groupLoading['Participants'] = true;
    this.relatedEntitiesService
      .getRelatedEventsParticipants(this.qparam, 1000)
      .subscribe(
        (participants) => {
          this.processParticipants(participants);
          this.fetched.add('Participants');
          this.groupLoading['Participants'] = false;
        },
        () => {
          this.infoLists['Participants'] = [];
          this.groupLoading['Participants'] = false;
        },
      );
  }

  private processParticipants(participants: any[]) {
    const countriesFilter2 = [];
    const sectorsFilter2 = [];
    this.infoLists['Participants'] = participants.map((element) => {
      if (element.entity.description.Country) {
        countriesFilter2.push(
          JSON.stringify({
            label: element.entity.description.Country.name,
            value: element.entity.description.Country.entity.alpha_id,
          }),
        );
      }
      if (element.entity.description.Sector) {
        sectorsFilter2.push(
          JSON.stringify({
            label: element.entity.description.Sector.name,
            value: element.entity.description.Sector.entity.alpha_id,
          }),
        );
      }
      return {
        name: element.entity.name,
        country: element.entity.description.Country
          ? {
              name: element.entity.description.Country.name,
              value: element.entity.description.Country.entity.alpha_id,
            }
          : null,
        sector: element.entity.description.Sector
          ? {
              name: element.entity.description.Sector.name,
              value: element.entity.description.Sector.entity.alpha_id,
            }
          : null,
        volume: Math.round(element.sentiment.volume_ratio * 10) / 10,
        tag: 'm:' + element.entity.compound_key,
        compound_key: element.entity.compound_key,
      };
    });
    this.countries2 = Array.from(new Set(countriesFilter2)).map((item) =>
      JSON.parse(item),
    );
    this.sectors2 = Array.from(new Set(sectorsFilter2)).map((item) =>
      JSON.parse(item),
    );
  }

  // Fetch portfolio data — only on Portfolio tab click (portfolio context only).
  private fetchPortfolio() {
    if (!this.routing.isFolio() || !this.portfolioApi.activePortfolio) return;

    this.groupLoading['Portfolio'] = true;
    const content = this.portfolioApi.activePortfolio.content;
    const eventIds: string[] | undefined = this.qparam?.event_ids
      ? String(this.qparam.event_ids).split(',')
      : undefined;

    forkJoin({
      portfolios: this.portfolioService.getPortfolio(this.qparam, content),
      eventsByParticipant: this.subEvents.fetchSubEventsItemized(
        this.qparam,
        content,
        undefined,
        eventIds,
      ),
    }).subscribe(
      (result) => {
        const filteredCompanies = this.findEventParticipants(
          result.eventsByParticipant,
          eventIds,
        );
        const portfolioList = result.portfolios.treemap.children.map(
          (element: any) => ({
            name: element.entity.name,
            volume: Math.round(element.volume_ratio * 10) / 10,
            tag: 'm:' + element.entity.compound_key,
          }),
        );
        const participantTags = new Set(filteredCompanies.map((el) => el.tag));
        this.infoLists['PortfolioParticipants'] = portfolioList.filter((item) =>
          participantTags.has(item.tag),
        );
        this.infoLists['PortfolioCompanies'] = portfolioList.filter(
          (item) => !participantTags.has(item.tag),
        );
        // Skip when event_ids is set — itemized response is a subset, not
        // the full catalog the Discover list expects.
        if (!eventIds) {
          this.populateEventsFromItemized(result.eventsByParticipant);
        }
        this.fetched.add('Portfolio');
        this.groupLoading['Portfolio'] = false;
      },
      () => {
        this.infoLists['PortfolioParticipants'] = [];
        this.infoLists['PortfolioCompanies'] = [];
        this.groupLoading['Portfolio'] = false;
      },
    );
  }

  private findEventParticipants(
    eventsByParticipant: Map<
      string,
      { count: number; event: { id: string } }[]
    >,
    eventIds: string[] | undefined,
  ) {
    const filteredCompanies: { tag: string }[] = [];
    for (const [compoundKey, events] of eventsByParticipant) {
      const matches = events.some((e) =>
        eventIds ? e.count > 0 && eventIds.includes(e.event.id) : e.count > 0,
      );
      if (matches) {
        filteredCompanies.push({ tag: 'm:' + compoundKey });
      }
    }
    return filteredCompanies;
  }

  private fetchDefaultTab() {
    const defaultTab = this.tabNames[0];
    if (defaultTab === 'Portfolio') {
      this.fetchPortfolio();
    } else if (defaultTab === 'Participants') {
      this.fetchParticipants();
    } else if (defaultTab === 'Discover' && !this.fetched.has('Events')) {
      this.fetchEvents();
    }
  }

  /**
   * Ensure data is fetched for the requested Discover group.
   * Companies/PNEs/Countries share a single API call.
   */
  private ensureGroupData(group: string) {
    if (this.fetched.has(group)) return;

    if (
      group === 'Companies' ||
      group === 'Potential Entities' ||
      group === 'Countries'
    ) {
      if (!this.fetched.has('Companies')) {
        this.fetchEntities();
      }
    } else if (group === 'Sources') {
      this.fetchSources();
    } else if (group === 'Events' && !this.fetched.has('Events')) {
      this.fetchEvents();
    }
  }

  // --- Navigation methods ---

  iParticipant(item) {
    const participant =
      this.activeParticipant === item.compound_key
        ? null
        : encodeURI(item.compound_key);
    this.router.navigate([], {
      queryParams: {
        participant: participant,
        eventype: null,
        eventid: null,
        tag: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  iTag(item) {
    const tag = this.activeTag === item.tag ? null : encodeURI(item.tag);
    this.router.navigate([], {
      queryParams: {
        tag: tag,
        eventype: null,
        eventid: null,
        participant: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  iEvent(item) {
    let eventType = null;
    let eventId = null;
    if (
      this.activeEvent.type !== item.type ||
      this.activeEvent.id !== item.id
    ) {
      eventType = item.type;
      eventId = item.id;
    }
    this.router.navigate([], {
      queryParams: {
        eventype: eventType,
        eventid: eventId,
        newsday: null,
        tag: null,
        participant: null,
      },
      queryParamsHandling: 'merge',
    });
  }
}
